import { useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Mail,
  Phone,
  User
} from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'
import { COUNTRIES } from '../data/countries'

const HERO_IMAGE =
  'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg'

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/

const STEPS = [
  { id: 1, title: 'Your details', hint: 'Tell us about you and your business' },
  { id: 2, title: 'Secure access', hint: 'Create your password and confirm agreements' }
]

const INPUT_CLASS =
  'input-field h-11 border-neutral-200 bg-white pl-11 pr-4 text-[15px] focus:border-neutral-900/20 focus:ring-neutral-900/25'

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-2" aria-label="Application progress">
      {STEPS.map((s, i) => {
        const active = step === s.id
        const done = step > s.id
        return (
          <div key={s.id} className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-poppinsSemiBold transition-colors ${
                active
                  ? 'bg-neutral-900 text-white'
                  : done
                    ? 'bg-neutral-900/10 text-neutral-900'
                    : 'bg-neutral-100 text-neutral-400'
              }`}
            >
              {done ? <Check className="h-3.5 w-3.5" aria-hidden /> : s.id}
            </div>
            <span
              className={`hidden truncate text-[11px] font-poppinsSemiBold uppercase tracking-wider sm:block ${
                active ? 'text-neutral-900' : 'text-neutral-400'
              }`}
            >
              {s.title}
            </span>
            {i < STEPS.length - 1 && (
              <div
                className={`mx-1 h-px flex-1 max-w-8 sm:max-w-12 ${done || active ? 'bg-neutral-300' : 'bg-neutral-200'}`}
                aria-hidden
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function PasswordStrength({ checks }) {
  const score = Object.values(checks).filter(Boolean).length
  const pct = (score / 5) * 100
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-poppinsSemiBold uppercase tracking-wider text-neutral-500">
          Password strength
        </span>
        <span className="text-[11px] font-poppinsMedium text-neutral-600">
          {score === 5 ? 'Strong' : score >= 3 ? 'Good' : score >= 1 ? 'Weak' : '—'}
        </span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            score <= 2 ? 'bg-amber-500' : score <= 4 ? 'bg-neutral-600' : 'bg-emerald-600'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[11px] font-poppinsRegular text-neutral-500 leading-relaxed">
        Use 8+ characters with upper & lower case, a number, and a special character (@$!%*?&).
      </p>
    </div>
  )
}

const TravelAdvisorRegisterPage = () => {
  const [step, setStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      termsAccepted: false,
      extranetMisuseAcknowledgment: false
    }
  })

  const password = watch('password') || ''
  const confirmPassword = watch('confirmPassword') || ''

  const passwordChecks = useMemo(
    () => ({
      length: password.length >= 8,
      lower: /[a-z]/.test(password),
      upper: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password)
    }),
    [password]
  )

  const passwordsMatch = password.length > 0 && password === confirmPassword

  const goToStep2 = async () => {
    const valid = await trigger(['name', 'email', 'phoneNumber', 'country', 'legalBusinessName'])
    if (valid) setStep(2)
  }

  const onSubmit = async (data) => {
    if (!data.termsAccepted) {
      toast.error('Please accept the terms and conditions')
      return
    }
    if (!PASSWORD_PATTERN.test(data.password)) {
      toast.error('Please meet all password requirements')
      return
    }
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setIsLoading(true)
    try {
      const response = await travelAgentService.register({
        name: data.name.trim(),
        email: data.email.trim(),
        phoneNumber: data.phoneNumber.trim(),
        country: data.country,
        legalBusinessName: data.legalBusinessName.trim(),
        password: data.password,
        termsAccepted: true,
        extranetMisuseAcknowledgment: !!data.extranetMisuseAcknowledgment
      })
      toast.success(response.message || 'Application submitted successfully')
      navigate('/verify-email', {
        state: {
          email: data.email.trim(),
          applicationId: response?.data?.applicationId
        }
      })
    } catch (error) {
      toast.error(error.message || 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-[100dvh] max-h-[100dvh] flex flex-col lg:flex-row overflow-hidden bg-white antialiased font-poppinsRegular text-neutral-800">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative min-h-0 overflow-hidden shrink-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/55" />

        <div className="absolute top-6 left-6 xl:top-8 xl:left-8 z-20">
          <button
            type="button"
            onClick={() => navigate('/luxurytravel-advisors')}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/25 px-4 py-2.5 text-[10px] font-poppinsSemiBold uppercase tracking-[0.2em] text-white backdrop-blur-md transition-colors hover:bg-black/40 hover:border-white/30"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            Back
          </button>
        </div>

        <div className="relative z-10 flex flex-1 flex-col justify-center px-10 xl:px-16 2xl:px-20 py-12">
          <div className="max-w-md rounded-3xl border border-white/15 bg-black/35 p-9 xl:p-10 shadow-2xl shadow-black/40 backdrop-blur-2xl">
            <div className="mb-6 border-b border-white/10 pb-6">
              <AxonallLogo color="text-white" size="text-3xl xl:text-4xl" />
              <p className="mt-3 text-[10px] font-poppinsSemiBold uppercase tracking-[0.35em] text-white/55">
                Axonall Reserve
              </p>
            </div>
            <p className="font-AXONALLPrimeRegular text-2xl xl:text-3xl font-medium leading-snug tracking-tight text-white">
              Join the advisor circle
            </p>
            <p className="mt-4 text-sm font-poppinsLight leading-relaxed text-white/80">
              Apply for membership in Axonall Reserve. After email verification, our team will review your
              application within 48 hours.
            </p>
            <ul className="mt-6 space-y-3 border-t border-white/10 pt-6 text-sm font-poppinsLight text-white/70">
              {[
                'Complete this short application',
                'Verify your email with a one-time code',
                'Receive approval and access Axonall Studio'
              ].map((text, i) => (
                <li key={text} className="flex gap-3">
                  <span className="font-poppinsSemiBold text-white/35 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Form column */}
      <div className="relative flex h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden px-4 sm:px-8 lg:bg-gradient-to-b lg:from-neutral-50 lg:via-white lg:to-neutral-100 lg:px-12 xl:px-16">
        <div className="pointer-events-none absolute inset-0 z-0 lg:hidden" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-black/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-100 via-white/88 to-white/72" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-neutral-50/95" />
        </div>

        <div
          className="pointer-events-none absolute -right-24 top-[12%] z-0 h-72 w-72 rounded-full bg-neutral-900/[0.04] blur-3xl max-lg:opacity-40"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-12 top-1/2 z-0 h-48 w-48 -translate-y-1/2 rounded-full bg-neutral-400/[0.06] blur-3xl max-lg:hidden"
          aria-hidden
        />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center py-6 lg:py-8">
          <div className="mb-6 flex items-center justify-between lg:hidden">
            <button
              type="button"
              onClick={() => (step === 1 ? navigate('/luxurytravel-advisors') : setStep(1))}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-2 text-[10px] font-poppinsSemiBold uppercase tracking-[0.18em] text-neutral-700 shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
            <AxonallLogo size="text-xl" color="text-neutral-900" />
          </div>

          <div className="mx-auto w-full max-w-2xl space-y-5">
            {/* Header */}
            <div className="text-center lg:text-left">
              <h1 className="font-AXONALLPrimeRegular text-2xl sm:text-[1.65rem] font-medium tracking-tight text-neutral-900">
                Apply for Axonall Reserve
              </h1>
              <p className="mt-1.5 text-sm font-poppinsRegular text-neutral-500">
                {STEPS[step - 1].hint}
              </p>
              <div className="mt-4">
                <StepIndicator step={step} />
              </div>
            </div>

            {/* Form card */}
            <div className="rounded-[1.5rem] border border-neutral-200/90 bg-white/95 p-6 sm:p-7 shadow-[0_20px_70px_rgba(15,23,42,0.08)] backdrop-blur-sm">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {step === 1 && (
                  <div className="space-y-4">
                    <FormSection title="About you">
                      <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                        <Field
                          id="name"
                          label="Full name"
                          icon={User}
                          error={errors.name?.message}
                          input={
                            <input
                              id="name"
                              autoComplete="name"
                              className={INPUT_CLASS}
                              placeholder="Jane Smith"
                              {...register('name', { required: 'Full name is required' })}
                            />
                          }
                        />
                        <Field
                          id="email"
                          label="Email address"
                          icon={Mail}
                          error={errors.email?.message}
                          input={
                            <input
                              id="email"
                              type="email"
                              autoComplete="email"
                              className={INPUT_CLASS}
                              placeholder="you@business.com"
                              {...register('email', {
                                required: 'Email is required',
                                pattern: {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: 'Enter a valid email address'
                                }
                              })}
                            />
                          }
                        />
                      </div>
                    </FormSection>

                    <FormSection title="Your business">
                      <Field
                        id="legalBusinessName"
                        label="Business name"
                        icon={Building2}
                        error={errors.legalBusinessName?.message}
                        input={
                          <input
                            id="legalBusinessName"
                            autoComplete="organization"
                            className={INPUT_CLASS}
                            placeholder="Legal or trading name"
                            {...register('legalBusinessName', {
                              required: 'Business name is required'
                            })}
                          />
                        }
                      />
                      <div className="mt-4 grid gap-4 sm:grid-cols-2 sm:gap-5">
                        <Field
                          id="phoneNumber"
                          label="Phone"
                          icon={Phone}
                          error={errors.phoneNumber?.message}
                          input={
                            <input
                              id="phoneNumber"
                              type="tel"
                              autoComplete="tel"
                              className={INPUT_CLASS}
                              placeholder="+1 555 000 0000"
                              {...register('phoneNumber', {
                                required: 'Phone number is required',
                                minLength: { value: 6, message: 'Enter a valid phone number' }
                              })}
                            />
                          }
                        />
                        <Field
                          id="country"
                          label="Country"
                          icon={Globe}
                          error={errors.country?.message}
                          hasChevron
                          input={
                            <select
                              id="country"
                              className={`${INPUT_CLASS} appearance-none pr-10`}
                              defaultValue=""
                              {...register('country', { required: 'Please select your country' })}
                            >
                              <option value="" disabled>
                                Select country
                              </option>
                              {COUNTRIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          }
                        />
                      </div>
                    </FormSection>

                    <button
                      type="button"
                      onClick={goToStep2}
                      className="typography-button mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-950 py-3 text-xs font-medium tracking-[0.2em] text-white shadow-lg shadow-neutral-900/15 transition-all hover:bg-black hover:shadow-xl"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <Field
                      id="password"
                      label="Password"
                      icon={Lock}
                      error={errors.password?.message}
                      input={
                        <>
                          <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className={`${INPUT_CLASS} pr-11`}
                            placeholder="Create a secure password"
                            {...register('password', {
                              required: 'Password is required',
                              validate: (v) =>
                                PASSWORD_PATTERN.test(v) ||
                                'Must include upper, lower, number, and special (@$!%*?&)'
                            })}
                          />
                          <PasswordToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                        </>
                      }
                    />

                    <PasswordStrength checks={passwordChecks} />

                    <Field
                      id="confirmPassword"
                      label="Confirm password"
                      icon={Lock}
                      error={
                        errors.confirmPassword?.message ||
                        (confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined)
                      }
                      input={
                        <>
                          <input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className={`${INPUT_CLASS} pr-11`}
                            placeholder="Re-enter your password"
                            {...register('confirmPassword', {
                              required: 'Please confirm your password',
                              validate: (v) => v === password || 'Passwords do not match'
                            })}
                          />
                          <PasswordToggle
                            show={showConfirmPassword}
                            onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                        </>
                      }
                    />

                    <div className="space-y-3 rounded-xl border border-neutral-100 bg-neutral-50/60 p-4">
                      <CheckboxField
                        register={register('termsAccepted', { required: 'Required' })}
                        showError={!!errors.termsAccepted}
                      >
                        I accept the{' '}
                        <a
                          href="https://axonall.com/termsConditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-poppinsMedium text-neutral-900 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900"
                        >
                          Terms & Conditions
                        </a>{' '}
                        and Axonall Studio policies.
                      </CheckboxField>
                      <CheckboxField register={register('extranetMisuseAcknowledgment')}>
                        I acknowledge responsible use of the advisor extranet and client data.
                      </CheckboxField>
                    </div>

                    <div className="flex gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 rounded-xl border border-neutral-200 bg-white py-3 text-xs font-poppinsSemiBold uppercase tracking-[0.15em] text-neutral-700 transition-colors hover:bg-neutral-50"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="typography-button flex flex-[1.35] items-center justify-center gap-2 rounded-xl bg-neutral-950 py-3 text-xs font-medium tracking-[0.2em] text-white shadow-lg shadow-neutral-900/15 transition-all hover:bg-black hover:shadow-xl disabled:opacity-60"
                      >
                        {isLoading ? (
                          <>
                            <LoadingSpinner size="sm" className="mr-1" />
                            Submitting…
                          </>
                        ) : (
                          'Submit application'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>

              {step === 1 && (
                <div className="mt-6 border-t border-neutral-100 pt-5">
                  <p className="text-center text-sm font-poppinsRegular text-neutral-600">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className="font-poppinsMedium text-neutral-900 underline decoration-neutral-300 underline-offset-4 hover:decoration-neutral-900"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FormSection({ title, children }) {
  return (
    <div>
      <p className="mb-3 text-[10px] font-poppinsSemiBold uppercase tracking-[0.22em] text-neutral-400">
        {title}
      </p>
      {children}
    </div>
  )
}

function PasswordToggle({ show, onToggle }) {
  return (
    <button
      type="button"
      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-neutral-400 transition-colors hover:text-neutral-700"
      onClick={onToggle}
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
    </button>
  )
}

function Field({ id, label, icon: Icon, error, input, hasChevron }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block text-[11px] font-poppinsSemiBold uppercase tracking-wider text-neutral-600"
      >
        {label}
      </label>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
          <Icon className="h-[18px] w-[18px] text-neutral-400" aria-hidden />
        </div>
        {input}
        {hasChevron && (
          <ChevronDown
            className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            aria-hidden
          />
        )}
      </div>
      {error && <p className="error-text">{error}</p>}
    </div>
  )
}

function CheckboxField({ register, showError, children }) {
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
          {...register}
        />
        <span className="text-sm font-poppinsRegular leading-relaxed text-neutral-600">{children}</span>
      </label>
      {showError && <p className="error-text mt-1">You must accept the terms to continue</p>}
    </div>
  )
}

export default TravelAdvisorRegisterPage
